import { useCallback } from 'react';
import { getMyBonusCodeBinding, validateBonusCode } from '@/app/apis';
import { ONE_MONTH, SITE } from '@/app/constants';
import { useStore, useStoreApi } from '@/app/providers/store';
import { safeStorage } from '@/app/utils/client';

interface BonusCodeBound {
  code: string;
  boundAt: number;
}

export const BONUS_CODE_BOUND_KEY = 'BONUS_CODE_BOUND_AT';

/** Explicit reads only: initialization owns display refresh; payment reads immediately before an order. */
export const useBonusCode = () => {
  const updateBonusCode = useStore((state) => state.updateBonusCode);
  const storeApi = useStoreApi();

  const resolveBonusCode = useCallback(
    async ({ forOrder = false } = {}): Promise<string | undefined> => {
      const ownerId = storeApi.getState().userInfo?.id;
      if (SITE !== 'china' || !ownerId) return undefined;
      const codeBeforeRequest = storeApi.getState().bonusCode;
      try {
        const { data, error } = await getMyBonusCodeBinding();
        const current = storeApi.getState();
        if (current.userInfo?.id !== ownerId) return undefined;
        // A manual bind may have finished while this older read was in flight.
        if (current.bonusCode !== codeBeforeRequest) return current.bonusCode || undefined;
        if (error || !data) return undefined;
        let code = data.code || null;
        if (code && !data.has_binding) {
          // Legacy orders retain the original 90-day window, including the existing browser timestamp.
          let bound: BonusCodeBound | null = null;
          try {
            bound = JSON.parse(safeStorage.getItem(BONUS_CODE_BOUND_KEY) || 'null');
          } catch {
            // A missing or unreadable record starts the legacy window on first display.
          }
          if (bound?.code !== code) {
            safeStorage.setItem(BONUS_CODE_BOUND_KEY, JSON.stringify({ code, boundAt: Date.now() }));
          } else if (!(Date.now() - bound.boundAt < 3 * ONE_MONTH)) {
            code = null;
          }
          if (code && forOrder) {
            const reply = await validateBonusCode({ path: { code } });
            const latest = storeApi.getState();
            if (latest.userInfo?.id !== ownerId) return undefined;
            if (latest.bonusCode !== codeBeforeRequest) return latest.bonusCode || undefined;
            if (reply.error || !reply.data?.valid) code = null;
          }
        }
        updateBonusCode(code);
        return code || undefined;
      } catch {
        return undefined;
      }
    },
    [storeApi, updateBonusCode],
  );

  return { refreshBonusCode: resolveBonusCode, resolveBonusCode };
};
